import { useEffect, useRef, useState } from 'react'
import maplibregl from 'maplibre-gl'
import { sidcGraphic, sidcSvg } from '../../utils/military'
import { isTacticalIcon, loadTacticalCollection, tacticalIconSvg } from '../../utils/tacticalIcons'

const parseStyle = (feature) => {
  try { return { ...JSON.parse(feature.style || '{}') } } catch { return {} }
}
const widthPx = (style) => (style.width === 'thin' ? 2 : style.width === 'thick' ? 6 : 3.5)
const dashOf = (style) => (style.dash === 'dash' ? 'dash' : style.dash === 'dot' ? 'dot' : 'solid')

export function useMilPlan(map, {
  milPlanFeatures, milPlanActive, milPlanTool, onMilPlanClick, onMilPlanGesture,
  selectedMilPlanFeature, onMilPlanFeatureSelect, onMilPlanFeatureChange,
  milPlanTextDraft, milPlanTextStyle, onMilPlanTextCommit, onMilPlanTextCancel,
}) {
  const markersRef = useRef([])
  const [styleTick, setStyleTick] = useState(0)
  const [iconTick, setIconTick] = useState(0)

  useEffect(() => {
    const sources = new Set()
    ;(milPlanFeatures || []).forEach((feature) => {
      const icon = parseStyle(feature).icon
      if (isTacticalIcon(icon)) sources.add(icon.split(':', 1)[0])
    })
    if (!sources.size) return
    let cancelled = false
    Promise.all([...sources].map(loadTacticalCollection)).then(() => {
      if (!cancelled) setIconTick((tick) => tick + 1)
    }).catch(() => {})
    return () => { cancelled = true }
  }, [milPlanFeatures])

  useEffect(() => {
    if (!map.current) return
    markersRef.current.forEach((marker) => marker.remove())
    markersRef.current = []
    const lineFeatures = []
    const layerHandlers = []

    const updateStyle = (feature, patch) => {
      if (feature._planIndex == null || !onMilPlanFeatureChange) return
      onMilPlanFeatureChange(feature._planIndex, { style: JSON.stringify({ ...parseStyle(feature), ...patch }) })
    }

    ;(milPlanFeatures || []).forEach((feature) => {
      let coords
      try { coords = JSON.parse(feature.geometry) } catch { return }
      const style = parseStyle(feature)
      if (feature.feature_type === 'marker' || feature.feature_type === 'label') {
        const [lon, lat] = Array.isArray(coords[0]) ? coords[0] : coords
        const selected = feature._planIndex != null && feature._planIndex === selectedMilPlanFeature
        const root = document.createElement('div')
        root.className = `milplan-marker${selected ? ' selected' : ''}`
        root.dataset.milplanFeature = feature._planIndex ?? ''
        const transform = document.createElement('div')
        transform.className = 'milplan-marker-transform'
        transform.style.transform = `rotate(${Number(style.rotation) || 0}deg)`
        const content = document.createElement('div')
        content.className = 'milplan-marker-content'
        let symbol = null
        let marker = null
        let markerOffset = [0, 0]
        let renderSymbol = () => {}
        if (feature.feature_type === 'marker') {
          symbol = document.createElement('div')
          symbol.className = 'milplan-marker-symbol'
          renderSymbol = (size) => {
            if (isTacticalIcon(style.icon)) {
              symbol.innerHTML = tacticalIconSvg(style.icon, size, feature.color || '#ffffff')
              transform.style.transformOrigin = `${size / 2}px ${size / 2}px`
              markerOffset = [0, 0]
            } else {
              const graphic = sidcGraphic(feature.sidc || '10031000001211000000', size)
              symbol.innerHTML = graphic.svg
              transform.style.transformOrigin = `${graphic.anchor.x}px ${graphic.anchor.y}px`
              markerOffset = [graphic.size.width / 2 - graphic.anchor.x, graphic.size.height / 2 - graphic.anchor.y]
            }
            marker?.setOffset(markerOffset)
          }
          renderSymbol(Number(style.size) || 36)
          content.appendChild(symbol)
          if (feature.label) {
            const caption = document.createElement('div')
            caption.className = 'milplan-marker-caption'; caption.textContent = feature.label
            content.appendChild(caption)
          }
        } else {
          content.textContent = feature.label || ''
          content.classList.add('milplan-label')
          content.style.color = feature.color || '#fff'
          content.style.fontFamily = style.fontFamily || 'Rajdhani, sans-serif'
          content.style.fontSize = `${Number(style.fontSize || style.size) || 16}px`
          content.style.background = style.background || 'transparent'
        }
        transform.appendChild(content)
        root.appendChild(transform)
        root.addEventListener('pointerdown', (event) => event.stopPropagation())
        root.addEventListener('click', (event) => { event.stopPropagation(); if (feature._planIndex != null) onMilPlanFeatureSelect?.(feature._planIndex) })

        if (selected && feature.feature_type === 'marker') {
          const rotate = document.createElement('button')
          rotate.type = 'button'; rotate.className = 'milplan-rotate-handle'; rotate.title = 'Faire pivoter'
          const resize = document.createElement('button')
          resize.type = 'button'; resize.className = 'milplan-resize-handle'; resize.title = 'Redimensionner'
          transform.append(rotate, resize)
          const beginHandle = (event, mode) => {
            event.preventDefault(); event.stopPropagation(); map.current.dragPan.disable()
            const projected = map.current.project([lon, lat]); const mapRect = map.current.getContainer().getBoundingClientRect()
            const center = { x: mapRect.left + projected.x, y: mapRect.top + projected.y }
            const startSize = Number(style.size) || 36
            const startDistance = Math.max(1, Math.hypot(event.clientX - center.x, event.clientY - center.y))
            let value = mode === 'resize' ? startSize : (Number(style.rotation) || 0)
            const move = (moveEvent) => {
              if (mode === 'resize') {
                const distance = Math.hypot(moveEvent.clientX - center.x, moveEvent.clientY - center.y)
                value = Math.max(20, Math.min(140, startSize * distance / startDistance))
                renderSymbol(value)
              } else {
                value = Math.round(Math.atan2(moveEvent.clientY - center.y, moveEvent.clientX - center.x) * 180 / Math.PI + 90)
                transform.style.transform = `rotate(${value}deg)`
              }
            }
            const end = () => {
              window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', end)
              map.current?.dragPan.enable()
              updateStyle(feature, mode === 'resize' ? { size: Math.round(value) } : { rotation: value })
            }
            window.addEventListener('pointermove', move); window.addEventListener('pointerup', end, { once: true })
          }
          resize.addEventListener('pointerdown', (event) => beginHandle(event, 'resize'))
          rotate.addEventListener('pointerdown', (event) => beginHandle(event, 'rotate'))
        }

        const editable = feature._planIndex != null && milPlanTool === 'select'
        marker = new maplibregl.Marker({ element: root, anchor: 'center', offset: markerOffset, draggable: editable }).setLngLat([lon, lat]).addTo(map.current)
        if (editable) marker.on('dragend', () => {
          const point = marker.getLngLat()
          onMilPlanFeatureChange?.(feature._planIndex, { geometry: JSON.stringify([point.lng, point.lat]) })
        })
        markersRef.current.push(marker)
      } else {
        lineFeatures.push({
          type: 'Feature',
          geometry: { type: feature.feature_type === 'polygon' ? 'Polygon' : 'LineString', coordinates: feature.feature_type === 'polygon' ? [coords] : coords },
          properties: { color: feature.color || '#e07070', kind: feature.feature_type, width: widthPx(style), dash: dashOf(style), planIndex: feature._planIndex ?? -1, selected: feature._planIndex != null && feature._planIndex === selectedMilPlanFeature },
        })
        if (feature.feature_type === 'arrow' && coords.length >= 2) {
          const heads = style.heads === 0 ? 0 : (style.heads === 2 ? 2 : 1)
          const addHead = (from, to) => {
            const bearing = (Math.atan2(to[0] - from[0], to[1] - from[1]) * 180) / Math.PI
            const element = document.createElement('div')
            element.className = 'milplan-arrow-head'; element.textContent = '▲'; element.style.color = feature.color || '#e07070'; element.style.fontSize = `${14 + widthPx(style) * 2}px`
            const head = new maplibregl.Marker({ element, anchor: 'center', rotation: bearing, rotationAlignment: 'map', pitchAlignment: 'map' }).setLngLat(to).addTo(map.current)
            markersRef.current.push(head)
          }
          if (heads >= 1) addHead(coords.at(-2), coords.at(-1))
          if (heads === 2) addHead(coords[1], coords[0])
        }
      }
    })

    const collection = { type: 'FeatureCollection', features: lineFeatures }
    const source = map.current.getSource('milplan-lines')
    if (!source) {
      if (!map.current.getLayer('regions-fill')) { map.current.once('idle', () => setStyleTick((tick) => tick + 1)); return }
      map.current.addSource('milplan-lines', { type: 'geojson', data: collection })
      const addLine = (id, dash, dasharray) => {
        const paint = { 'line-color': ['get', 'color'], 'line-width': ['case', ['boolean', ['get', 'selected'], false], ['+', ['coalesce', ['get', 'width'], 3.5], 3], ['coalesce', ['get', 'width'], 3.5]] }
        if (dasharray) paint['line-dasharray'] = dasharray
        map.current.addLayer({ id, type: 'line', source: 'milplan-lines', filter: ['all', ['==', ['geometry-type'], 'LineString'], ['==', ['get', 'dash'], dash]], paint })
      }
      addLine('milplan-lines-solid', 'solid', null); addLine('milplan-lines-dash', 'dash', [2.2, 1.4]); addLine('milplan-lines-dot', 'dot', [0.4, 1.6])
      map.current.addLayer({ id: 'milplan-polygons', type: 'fill', source: 'milplan-lines', filter: ['==', ['geometry-type'], 'Polygon'], paint: { 'fill-color': ['get', 'color'], 'fill-opacity': ['case', ['boolean', ['get', 'selected'], false], .32, .18] } })
      map.current.addLayer({ id: 'milplan-polygons-outline', type: 'line', source: 'milplan-lines', filter: ['==', ['geometry-type'], 'Polygon'], paint: { 'line-color': ['get', 'color'], 'line-width': ['coalesce', ['get', 'width'], 2] } })
    } else source.setData(collection)

    const selectLine = (event) => {
      const index = Number(event.features?.[0]?.properties?.planIndex)
      if (index >= 0) { event.originalEvent?.stopPropagation?.(); onMilPlanFeatureSelect?.(index) }
    }
    ;['milplan-lines-solid', 'milplan-lines-dash', 'milplan-lines-dot', 'milplan-polygons'].forEach((id) => {
      if (!map.current.getLayer(id)) return
      map.current.on('click', id, selectLine); layerHandlers.push(id)
    })
    return () => {
      layerHandlers.forEach((id) => { try { map.current?.off('click', id, selectLine) } catch { /* gone */ } })
      markersRef.current.forEach((marker) => marker.remove()); markersRef.current = []
    }
  }, [map, milPlanFeatures, selectedMilPlanFeature, milPlanTool, onMilPlanFeatureSelect, onMilPlanFeatureChange, styleTick, iconTick])

  useEffect(() => {
    if (!map.current || !milPlanTextDraft?.point) return
    const element = document.createElement('div')
    element.className = 'milplan-inline-text'; element.contentEditable = 'true'; element.role = 'textbox'; element.dataset.placeholder = 'Écrivez…'
    element.style.color = milPlanTextStyle?.color || '#fff'; element.style.fontFamily = milPlanTextStyle?.fontFamily || 'Rajdhani, sans-serif'; element.style.fontSize = `${Number(milPlanTextStyle?.fontSize) || 16}px`; element.style.background = milPlanTextStyle?.transparentBackground === false ? (milPlanTextStyle?.background || '#111') : 'transparent'
    const marker = new maplibregl.Marker({ element, anchor: 'center' }).setLngLat(milPlanTextDraft.point).addTo(map.current)
    let finished = false
    const finish = (cancel = false) => {
      if (finished) return
      finished = true
      if (cancel) onMilPlanTextCancel?.()
      else onMilPlanTextCommit?.(element.textContent || '')
    }
    element.addEventListener('pointerdown', (event) => event.stopPropagation())
    element.addEventListener('keydown', (event) => { if (event.key === 'Escape') { event.preventDefault(); finish(true) } if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) { event.preventDefault(); finish() } })
    element.addEventListener('blur', () => finish(), { once: true }); requestAnimationFrame(() => element.focus())
    return () => { finished = true; marker.remove() }
  }, [map, milPlanTextDraft, milPlanTextStyle, onMilPlanTextCommit, onMilPlanTextCancel])

  useEffect(() => {
    if (!map.current || !milPlanActive || !onMilPlanClick) return
    let start = null
    const click = (event) => {
      if (['line', 'arrow', 'freehand', 'rectangle', 'circle'].includes(milPlanTool)) return
      if (milPlanTool === 'select') {
        const layers = ['milplan-lines-solid', 'milplan-lines-dash', 'milplan-lines-dot', 'milplan-polygons'].filter((id) => map.current.getLayer(id))
        if (layers.length && map.current.queryRenderedFeatures(event.point, { layers }).length) return
      }
      onMilPlanClick({ lng: event.lngLat.lng, lat: event.lngLat.lat })
    }
    let path = []
    const dragTools = ['line', 'arrow', 'freehand', 'rectangle', 'circle']
    const down = (event) => { if (!dragTools.includes(milPlanTool) || event.originalEvent?.button !== 0) return; start = [event.lngLat.lng, event.lngLat.lat]; path = [start]; map.current.dragPan.disable(); event.preventDefault() }
    const move = (event) => { if (!start) return; const point = [event.lngLat.lng, event.lngLat.lat]; path = milPlanTool === 'freehand' ? [...path, point] : [start, point]; onMilPlanGesture?.(path, false) }
    const up = (event) => { if (!start) return; const end = [event.lngLat.lng, event.lngLat.lat]; path = milPlanTool === 'freehand' ? [...path, end] : [start, end]; onMilPlanGesture?.(path, true); start = null; path = []; map.current.dragPan.enable() }
    map.current.on('click', click); map.current.on('mousedown', down); map.current.on('mousemove', move); map.current.on('mouseup', up)
    map.current.getCanvas().style.cursor = milPlanTool === 'select' ? 'default' : 'crosshair'
    return () => { map.current?.off('click', click); map.current?.off('mousedown', down); map.current?.off('mousemove', move); map.current?.off('mouseup', up); if (start) map.current?.dragPan.enable(); if (map.current) map.current.getCanvas().style.cursor = '' }
  }, [map, milPlanActive, milPlanTool, onMilPlanClick, onMilPlanGesture])
}
